/**
 * use-platform-org-manage.ts — Hooks cho Platform → Tổ chức → chi tiết (các tab).
 * API: /platform/orgs/:orgId/* (backend `platform/org-manage-routes.ts`).
 * Mỗi tab một query riêng — chỉ chạy khi tab được mở (Radix Tabs unmount tab ẩn).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type { OrgDisplayStatus } from '@/hooks/use-platform'

type Paged<T> = { items: T[]; total: number; page: number; limit: number }
const k = (orgId: string, ...rest: unknown[]) => ['platform', 'org', orgId, ...rest] as const

// ── Kiểu dữ liệu ──────────────────────────────────────────────────
export interface BillingLimits {
  members: number | null
  officialChannels: number | null
  personalChannels: number | null
  activeContactsPerMonth: number | null
  aiRepliesPerDay: number | null
}
export interface Quotas { members: number; officialChannels: number; personalChannels: number }

export interface BillingSummary {
  plan: string | null
  effectivePlan: string | null
  wasDowngraded: boolean
  expiresAt: string | null
  status: OrgDisplayStatus
  planName: string | null
  limits: BillingLimits
  purchased: Quotas | null
  /** Phần "Mua thêm" còn hiệu lực đã cộng trong `purchased`. */
  addonPurchased?: Quotas | null
  /** Gói trả phí còn hạn — khách chỉ nâng gói / mua thêm (quản trị đổi tay vẫn được, có cảnh báo). */
  upgradeOnly?: boolean
  usage: {
    members: number
    channelsOfficial: number
    channelsPersonal: number
    contacts: number
    activeContacts30d: number
    aiToday: number
    aiDailyLimit: number | null
  }
}

export interface OrgOverview {
  org: {
    id: string; name: string; status: string; expiresAt: string | null; plan: string | null
    adminNotes: string | null; createdAt: string; updatedAt: string
    displayStatus: OrgDisplayStatus; expiringSoon: boolean
    /** Đã xoá mềm (null = đang dùng) — chỉ còn khôi phục / xoá vĩnh viễn. */
    deletedAt?: string | null
    /** Tự xoá vĩnh viễn lúc này (null = không tự xoá). */
    purgeAt?: string | null
    deleteReason?: string | null
    /** Đang xoá vĩnh viễn (chạy nền). */
    purging?: boolean
    /** Số ngày giữ tổ chức đã xoá trước khi tự xoá vĩnh viễn (0 = không tự xoá). */
    purgeAfterDays?: number
  }
  /** Thông tin công ty (khoá company.* — cùng Cài đặt → Thông tin công ty của khách). */
  company?: Record<CompanyKey, string>
  owners: Array<{ id: string; fullName: string; email: string; phone?: string | null; isActive: boolean }>
  billing: BillingSummary | null
  stats: { members: number; contacts: number; conversations: number; messages30d: number; systemRoles: number }
}

export interface CatalogPlan {
  id: string; name: string; purchasable: boolean; hidden: boolean; monthlyPrice: number | null; included: Quotas
}

/** Hợp đồng gói Doanh nghiệp (C2): null từng mục = không giới hạn. */
export interface ContractLimits {
  members: number | null; officialChannels: number | null; personalChannels: number | null
  activeContactsPerMonth: number | null; aiRepliesPerDay: number | null
}

export interface OrgBilling {
  summary: BillingSummary | null
  contract: ContractLimits | null
  isEnterprise: boolean
  currentOrder: { id: string; periodStart: string | null; periodEnd: string | null; provider: string; paidAt: string | null } | null
  plans: CatalogPlan[]
  orderStats: { byStatus: Record<string, number>; totalPaidAmount: number }
}

export interface OrgOrderRow {
  id: string; planId: string; planName: string; cycle: string; amount: number; provider: string; status: string
  paidAt: string | null; periodStart: string | null; periodEnd: string | null; createdAt: string
  invoiceStatus: string; invoiceNo: string | null; appTransId: string | null; zpTransId: string | null
  paymentCode: string | null; days: number | null; creditUsed: number; manualReason: string | null
  /** 'addon' = đơn "Mua thêm" cho kỳ hiện tại (server cũ: không có). */
  kind?: 'plan' | 'addon'
  /** Cách đơn áp vào gói: new | extend | requantity | change_plan | addon. */
  mode?: string | null
}

export interface QuoteLine { key: string; label: string; quantity: number; included: number; extra: number; monthly: number }
export interface OrderQuote {
  planId: string; planName: string; days: number; quantities: Quotas; basePerMonth: number; lines: QuoteLine[]
  perMonth: number; subtotal: number; discountPercent: number; discount: number; grossTotal: number
  credit: { amount: number; fromPlanName: string; remainingDays: number; applied: number } | null
  extraDays: number; totalDays: number; total: number
  /** VAT cộng thêm (Q2). Đơn cũ: không có. */
  vat?: { percent: number; preTax: number; amount: number }
  manual?: { reason?: string; adminId?: string }
  kind?: 'plan' | 'addon'
  mode?: string
  addon?: { from: Quotas; delta: Quotas; periodEnd: string; remainingDays: number } | null
}

export interface OrgOrderDetail {
  id: string; planId: string; cycle: string; amount: number; currency: string; provider: string; status: string
  quote: OrderQuote | null
  appTransId: string | null; zpTransId: string | null; paymentCode: string | null; providerTxnId: string | null
  returnCode: number | null; paidAt: string | null; periodStart: string | null; periodEnd: string | null
  expiresAt: string | null; createdAt: string; updatedAt: string
  invoiceInfo: Record<string, unknown> | null; invoiceStatus: string; invoiceNo: string | null
  invoiceUrl: string | null; invoiceXmlUrl: string | null; invoiceError: string | null; invoiceAttempts: number
  invoiceRequestedAt: string | null; invoiceIssuedAt: string | null
  createdBy: { kind: 'user' | 'platform'; name: string; email: string | null } | null
}

export const COMPANY_KEYS = [
  'company.name', 'company.phone', 'company.email', 'company.website', 'company.taxCode', 'company.address',
] as const
export type CompanyKey = (typeof COMPANY_KEYS)[number]

export interface OrgMember {
  id: string; email: string; fullName: string; role: string; roleName: string | null; isActive: boolean
  createdAt: string; lastLoginAt: string | null; presence: string; activeSessions: number
  phone?: string | null; roleId?: string | null; teamId?: string | null; teamName?: string | null
  /** Đang đăng nhập được bằng Google. */
  googleLinked?: boolean
  /** Bị quản trị hệ thống khoá — tổ chức không tự mở lại (C6). */
  lockedByPlatform?: boolean
}

export interface OrgChannel {
  id: string; platform: number; displayName: string | null; phone: string | null; status: string
  isDisabled: boolean; lockedByPlatform?: boolean; deletedAt: string | null; createdAt: string; lastConnectedAt: string | null
  tokenExpiresAt: string | null; owner: { id: string; fullName: string; email: string } | null
  state: 'connected' | 'disconnected' | 'disabled' | 'deleted'; lastActivityAt: string | null; conversations: number
}

export interface UsagePoint {
  day: string; inbound: number; outbound: number; newContacts: number; newConversations: number
  aiReplies: number; aiCalls: number; aiCostUsd: number; aiTokens: number
}
export interface OrgUsage { days: number; series: UsagePoint[]; totals: Omit<UsagePoint, 'day'> }

export interface ActivityLogItem {
  id: string; createdAt: string; actor: { id: string; fullName: string; email: string } | null; actorLabel: string
  action: string; actionLabel: string; moduleLabel: string; targetName: string | null; detailText: string
}
export interface LoginLogItem {
  id: string; createdAt: string; success: boolean; method: string; ip: string | null; browser: string | null
  os: string | null; deviceLabel: string | null; typeLabel: string; failureLabel: string | null; ipChanged: boolean
  user: { id: string; fullName: string; email: string } | null
}
export interface PlatformLogItem {
  id: string; createdAt: string; action: string; admin: { id: string; fullName: string; email: string } | null
  targetUserId: string | null; targetUser: { id: string; fullName: string; email: string } | null
  reason: string | null; meta: Record<string, unknown>
}
export interface LogFilters {
  users: Array<{ id: string; fullName: string; email: string }>
  modules: Array<{ id: string; label: string }>
  platformActions: string[]
}

export type LogKind = 'activity' | 'logins' | 'platform'
export type LogQuery = Record<string, string | number | undefined>

// ── Queries ───────────────────────────────────────────────────────
const get = async <T,>(url: string, params?: object) => (await platformApi.get<T>(url, { params })).data

export const useOrgOverview = (orgId: string) =>
  useQuery({ queryKey: k(orgId, 'overview'), queryFn: () => get<OrgOverview>(`/platform/orgs/${orgId}/overview`) })

export const useOrgBilling = (orgId: string) =>
  useQuery({ queryKey: k(orgId, 'billing'), queryFn: () => get<OrgBilling>(`/platform/orgs/${orgId}/billing`) })

export const useOrgOrders = (orgId: string, params: { status?: string; page: number; limit: number }) =>
  useQuery({
    queryKey: k(orgId, 'orders', params),
    queryFn: () => get<Paged<OrgOrderRow>>(`/platform/orgs/${orgId}/orders`, params),
    placeholderData: keepPreviousData,
  })

export const useOrgOrder = (orgId: string, orderId: string | null) =>
  useQuery({
    queryKey: k(orgId, 'order', orderId),
    queryFn: () => get<OrgOrderDetail>(`/platform/orgs/${orgId}/orders/${orderId}`),
    enabled: !!orderId,
  })

export const useOrgMembers = (orgId: string, params: { search?: string; page: number; limit: number }) =>
  useQuery({
    queryKey: k(orgId, 'members', params),
    queryFn: () => get<Paged<OrgMember>>(`/platform/orgs/${orgId}/members`, params),
    placeholderData: keepPreviousData,
  })

export const useOrgChannels = (orgId: string, params: { includeDeleted: boolean; page: number; limit: number }) =>
  useQuery({
    queryKey: k(orgId, 'channels', params),
    queryFn: () =>
      get<Paged<OrgChannel> & { summary: { connected: number; disconnected: number; disabled: number } }>(
        `/platform/orgs/${orgId}/channels`,
        { ...params, includeDeleted: params.includeDeleted ? '1' : undefined },
      ),
    placeholderData: keepPreviousData,
  })

export const useOrgUsage = (orgId: string, days: number) =>
  useQuery({
    queryKey: k(orgId, 'usage', days),
    queryFn: () => get<OrgUsage>(`/platform/orgs/${orgId}/usage`, { days }),
    placeholderData: keepPreviousData,
  })

export const useOrgLogFilters = (orgId: string) =>
  useQuery({
    queryKey: k(orgId, 'log-filters'),
    queryFn: () => get<LogFilters>(`/platform/orgs/${orgId}/logs/filters`),
    staleTime: 60_000,
  })

export function useOrgLogs<T>(orgId: string, kind: LogKind, params: LogQuery) {
  return useQuery({
    queryKey: k(orgId, 'logs', kind, params),
    queryFn: () => get<Paged<T>>(`/platform/orgs/${orgId}/logs/${kind}`, params),
    placeholderData: keepPreviousData,
  })
}

/**
 * Tải CSV nhật ký (qua platformApi để kèm token platform). Trả `truncated` khi máy chủ cắt ở giới hạn
 * (C13 — 100.000 dòng đầu) để giao diện báo "hãy lọc hẹp".
 */
export async function downloadOrgLogCsv(
  orgId: string, kind: LogKind, params: LogQuery,
): Promise<{ truncated: boolean; total: number; max: number }> {
  const res = await platformApi.get<Blob>(`/platform/orgs/${orgId}/logs/${kind}/export`, { params, responseType: 'blob' })
  const info = exportInfo(res.headers as Record<string, unknown>)
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `${kind === 'activity' ? 'nhat-ky-hoat-dong' : kind === 'logins' ? 'nhat-ky-dang-nhap' : 'nhat-ky-quan-tri-platform'}.csv`
  a.click()
  URL.revokeObjectURL(url)
  return info
}

/** Đọc header X-Export-* của phản hồi xuất CSV. */
export function exportInfo(headers: Record<string, unknown>): { truncated: boolean; total: number; max: number } {
  const h = (k: string) => String(headers[k] ?? headers[k.toLowerCase()] ?? '')
  return {
    truncated: h('x-export-truncated') === '1',
    total: Number(h('x-export-total')) || 0,
    max: Number(h('x-export-max')) || 0,
  }
}

// ── Mutations ─────────────────────────────────────────────────────
/** Mutation chung: gọi xong làm mới toàn bộ dữ liệu của tổ chức + danh sách tổ chức. */
function useOrgMutation<V, R>(orgId: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'org', orgId] })
      qc.invalidateQueries({ queryKey: ['platform', 'orgs'] })
      qc.invalidateQueries({ queryKey: ['platform', 'overview'] })
    },
  })
}

const base = (orgId: string) => `/platform/orgs/${orgId}`

export const useChangeOrgPlan = (orgId: string) =>
  useOrgMutation(orgId, async (v: { planId: string; reason: string; expiresAt?: string }) => (await platformApi.post(`${base(orgId)}/plan`, v)).data)

export const useExtendOrg = (orgId: string) =>
  useOrgMutation(orgId, async (v: { days: number; planId?: string; reason: string; quantities?: Partial<Quotas>; idempotencyKey?: string }) =>
    (await platformApi.post<{ expiresAt: string | null }>(`${base(orgId)}/extend`, v)).data)

export const useSaveOrgContract = (orgId: string) =>
  useOrgMutation(orgId, async (v: { expiresAt: string | null; limits: ContractLimits | null; reason: string }) =>
    (await platformApi.put<{ expiresAt: string | null; contract: ContractLimits | null }>(`${base(orgId)}/contract`, v)).data)

export const useSetOrgExpiry = (orgId: string) =>
  useOrgMutation(orgId, async (v: { expiresAt: string | null; reason: string }) => (await platformApi.post(`${base(orgId)}/license`, v)).data)

export const useSetMemberStatus = (orgId: string) =>
  useOrgMutation(orgId, async (v: { userId: string; isActive: boolean; reason: string }) =>
    (await platformApi.patch(`${base(orgId)}/members/${v.userId}/status`, { isActive: v.isActive, reason: v.reason })).data)

export const useTransferOwner = (orgId: string) =>
  useOrgMutation(orgId, async (v: { userId: string; reason: string }) =>
    (await platformApi.post(`${base(orgId)}/members/${v.userId}/transfer-owner`, { reason: v.reason })).data)

export const useRevokeMemberSessions = (orgId: string) =>
  useOrgMutation(orgId, async (v: { userId: string; reason: string }) =>
    (await platformApi.post<{ revokedSessions: number }>(`${base(orgId)}/members/${v.userId}/revoke-sessions`, { reason: v.reason })).data)

export const useSetChannelDisabled = (orgId: string) =>
  useOrgMutation(orgId, async (v: { channelId: string; isDisabled: boolean; reason: string }) =>
    (await platformApi.patch(`${base(orgId)}/channels/${v.channelId}`, { isDisabled: v.isDisabled, reason: v.reason })).data)

export const useUpdateOrgSettings = (orgId: string) =>
  useOrgMutation(orgId, async (v: { name?: string; adminNotes?: string | null; reason?: string }) =>
    (await platformApi.patch(`${base(orgId)}/settings`, v)).data)

export const useSetOrgSuspended = (orgId: string) =>
  useOrgMutation(orgId, async (v: { suspended: boolean; reason: string }) =>
    (await platformApi.post<{ revokedSessions: number }>(`${base(orgId)}/status`, v)).data)

export const useReseedRoles = (orgId: string) =>
  useOrgMutation(orgId, async (v: { reason?: string }) =>
    (await platformApi.post<{ roles: number; usersLinked: number }>(`${base(orgId)}/reseed-roles`, v)).data)

// ── Hạn mức AI (endpoint sẵn có ở platform-ops-routes) ────────────
export interface OrgAiQuota {
  orgId: string; orgName: string
  /** Cài đặt của TỔ CHỨC (chỉ xem ở Platform). */
  maxDaily: number | null; enabled: boolean | null; autoReplyEnabled: boolean | null
  /** Khung ba tầng — khoá / trần do Platform đặt (server cũ: undefined). */
  platformAiDisabled?: boolean | null
  platformMaxDaily?: number | null
}
export const useOrgAiQuota = (orgId: string) =>
  useQuery({ queryKey: k(orgId, 'ai-quota'), queryFn: () => get<OrgAiQuota>(`/platform/orgs/${orgId}/ai-quota`) })

/** Ghi khoá / trần của Platform (`platformAiDisabled`, `platformMaxDaily`) — không đụng cài đặt của tổ chức. */
export const useSaveOrgAiQuota = (orgId: string) =>
  useOrgMutation(orgId, async (v: { platformAiDisabled?: boolean; platformMaxDaily?: number | null }) =>
    (await platformApi.patch(`${base(orgId)}/ai-quota`, v)).data)

// ── Hồ sơ tổ chức + xoá tổ chức (backend platform/org-profile-routes.ts) ──
export interface MemberOptions {
  roles: Array<{ id: string; name: string; systemKey: string | null }>
  teams: Array<{ id: string; name: string }>
}
export const useMemberOptions = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: k(orgId, 'member-options'),
    queryFn: () => get<MemberOptions>(`/platform/orgs/${orgId}/member-options`),
    enabled,
    staleTime: 60_000,
  })

export const useSaveOrgCompany = (orgId: string) =>
  useOrgMutation(orgId, async (v: { values: Partial<Record<CompanyKey, string>>; reason?: string }) =>
    (await platformApi.put<{ company: Record<CompanyKey, string>; changed: string[] }>(`${base(orgId)}/company`, v)).data)

export interface MemberPatch {
  fullName?: string; email?: string; phone?: string | null; roleId?: string; teamId?: string | null; reason?: string
}
export const useUpdateOrgMember = (orgId: string) =>
  useOrgMutation(orgId, async (v: MemberPatch & { userId: string }) => {
    const { userId, ...body } = v
    return (await platformApi.patch<{ changed: string[]; revokedSessions: number }>(`${base(orgId)}/members/${userId}`, body)).data
  })

export const useUnlinkMemberGoogle = (orgId: string) =>
  useOrgMutation(orgId, async (v: { userId: string; reason?: string }) =>
    (await platformApi.post(`${base(orgId)}/members/${v.userId}/unlink-google`, { reason: v.reason })).data)

export const useDeleteOrg = (orgId: string) =>
  useOrgMutation(orgId, async (v: { reason: string }) =>
    (await platformApi.post<{ deletedAt: string; purgeAt: string | null; disabledChannels: number; revokedSessions: number }>(
      `${base(orgId)}/delete`, v)).data)

export const useRestoreOrg = (orgId: string) =>
  useOrgMutation(orgId, async (v: { reason: string }) =>
    (await platformApi.post<{ status: string; reopenedChannels: number; reconnecting: number }>(`${base(orgId)}/restore`, v)).data)

export const usePurgeOrg = (orgId: string) =>
  useOrgMutation(orgId, async (v: { reason: string; confirmName: string }) =>
    (await platformApi.post<{ purging: boolean }>(`${base(orgId)}/purge`, v)).data)
